// src/components/PhoneInputField.tsx
import * as React from 'react';
import {defaultCountries, FlagImage, parseCountry, usePhoneInput, removeDialCode} from 'react-international-phone';
import {Select, SelectOption} from "./Select";
import {useEffect} from "react";

interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
    label: string;
}

export const PhoneInputField = React.forwardRef<HTMLInputElement, Props>((props, ref) => {
    const {label, type, value, onChange, ...rest} = props;

    const {inputValue, handlePhoneValueChange, inputRef, country, setCountry} = usePhoneInput({
        defaultCountry: 'br',
        disableDialCodeAndPrefix: true,
        value: typeof value === 'string' ? value : String(value),
        countries: defaultCountries,
        onChange: ({phone}) => onChange && onChange({target: {value: phone}} as React.ChangeEvent<HTMLInputElement>),
    });

    // Combine the refs - both the one from usePhoneInput and the forwarded ref
    const combinedRef = (node: HTMLInputElement) => {
        if (typeof ref === 'function') {
            ref(node);
        } else if (ref) {
            ref.current = node;
        }

        // Apply the ref from usePhoneInput
        if (inputRef) {
            inputRef.current = node;
        }
    };

    return (
        <div>
            <label className="block font-bold mb-1">
                {label}
            </label>
            <div className="relative flex items-center gap-[3px]">
                <Select
                    tabIndex={-1}
                    hideArrow={true}
                    value={country.iso2}
                    onChange={(e) => {
                        setCountry(e.target.value as string);
                    }}
                    className="rounded-l-full rounded-r-none pr-2 bg-background-green text-tertiary border border-border w-full outline-none pl-4 py-2 flex items-center relative cursor-pointer"
                    options={defaultCountries.map((c) => {
                        const mCountry = parseCountry(c);
                        return {
                            value: mCountry.iso2,
                            label: (
                                <div className="flex items-center gap-2">
                                    <FlagImage
                                        iso2={mCountry.iso2}
                                        className="w-5"
                                    />
                                    <span className="text-14 font-normal text-tertiary">
                                        {mCountry.name}
                                    </span>
                                    <span className="text-14 font-normal text-tertiary">
                                        +{mCountry.dialCode}
                                    </span>
                                </div>
                            ),
                        }
                    })}
                    renderSelectedValue={(option) => (
                        <div className="flex items-center gap-2">
                            {(() => {
                                const foundCountry = defaultCountries.find(c => parseCountry(c).iso2 === option?.value);
                                return foundCountry ? '+' + parseCountry(foundCountry).dialCode : '';
                            })()}
                        </div>
                    )}
                />
                <input
                    {...rest}
                    value={inputValue}
                    onChange={handlePhoneValueChange}
                    className="placeholder-tertiary text-tertiary font-normal text-14 bg-transparent border border-border rounded-r-full w-full outline-none pr-20 pl-2 py-2 "
                />
                {/*
                    Helper input to have both dialcode and inputvalue at
                    same time and make zod return and validate full number
                    while keeping the custom select for the dialcode
                 */}
                <input
                    className="hidden"
                    ref={combinedRef}
                    value={"+" + country.dialCode + " " + inputValue}
                    readOnly={true}
                />
            </div>
        </div>
    );
});